import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * One shared player for voice previews: playing a second clip stops the first, and pressing the
 * playing one stops it. `playing` is the URL being played (null when silent); `failed` is the URL
 * that could not be played, so the row can say so.
 */
export function usePreviewAudio() {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState<string | null>(null)
  const [failed, setFailed] = useState<string | null>(null)

  const stop = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.onended = null
      audio.onerror = null
      audioRef.current = null
    }
    setPlaying(null)
  }, [])

  const toggle = useCallback(
    (url: string) => {
      if (playing === url) {
        stop()
        return
      }
      stop()
      setFailed(null)
      const audio = new Audio(url)
      audioRef.current = audio
      audio.onended = () => {
        if (audioRef.current === audio) stop()
      }
      audio.onerror = () => {
        if (audioRef.current === audio) {
          stop()
          setFailed(url)
        }
      }
      setPlaying(url)
      audio.play().catch(() => {
        if (audioRef.current === audio) {
          stop()
          setFailed(url)
        }
      })
    },
    [playing, stop],
  )

  // Never keep playing after the dialog or page that started it is gone.
  useEffect(() => () => audioRef.current?.pause(), [])

  return { playing, failed, toggle, stop }
}
